import ClickAwayListener from '@mui/material/ClickAwayListener';
import Tooltip from '@mui/material/Tooltip';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useZIndex } from '#components/cad-renderer/context/z-index-context.js';

import type { PopperPlacementType, PopperProps } from '@mui/material/Popper';
import type { TooltipProps } from '@mui/material/Tooltip';
import type { KeyboardEvent, ReactElement, ReactNode } from 'react';

type PopperInstance = NonNullable<PopperProps['popperRef']> extends React.Ref<infer T> ? T : never;

const TOOLTIP_VIEWPORT_PADDING = 8;

/** The props a {@link PinnablePreviewTooltip} trigger must spread onto the element that opens it. */
export interface PinnablePreviewTriggerProps {
	ref: (node: HTMLElement | null) => void;
	'aria-expanded': boolean;
	'aria-haspopup': 'true';
	onClick: () => void;
	onFocus: () => void;
	onKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
}

/** Props for {@link PinnablePreviewTooltip}. */
export interface PinnablePreviewTooltipProps {
	/** Renders the preview; `close` dismisses it, e.g. before an action inside it navigates away. */
	title: (close: () => void) => ReactNode;
	/** Renders the trigger, which must spread the given props onto a focusable element. */
	children: (triggerProps: PinnablePreviewTriggerProps) => ReactElement;
	/** Where the preview prefers to open; MUI's tooltip default when omitted. */
	placement?: TooltipProps['placement'];
	/** Where the preview may open instead when the preferred side lacks room, in order of preference. */
	fallbackPlacements: readonly PopperPlacementType[];
	/** Called each time the preview is asked to open, so content can be fetched on first demand. */
	onOpen?: () => void;
}

/**
 * A preview that opens while its trigger is hovered or focused, and stays pinned open once the trigger is
 * clicked until it is clicked again, clicked away from, or dismissed with Escape. The preview stays inside the
 * viewport, scrolls when taller than it, and repositions as its content resizes.
 */
export function PinnablePreviewTooltip({
	title,
	children,
	placement,
	fallbackPlacements,
	onOpen,
}: PinnablePreviewTooltipProps) {
	const [open, setOpen] = useState(false);
	const [pinned, setPinned] = useState(false);
	const triggerRef = useRef<HTMLElement | null>(null);
	const contentRef = useRef<HTMLDivElement | null>(null);
	const popperRef = useRef<PopperInstance | null>(null);
	const resizeObserverRef = useRef<ResizeObserver | null>(null);
	const zIndex = useZIndex('popover');

	const show = useCallback(() => {
		onOpen?.();
		setOpen(true);
	}, [onOpen]);

	const close = useCallback(() => {
		setPinned(false);
		setOpen(false);
	}, []);

	const handleTooltipClose = useCallback(() => {
		if (!pinned) {
			setOpen(false);
		}
	}, [pinned]);

	const handleTriggerClick = useCallback(() => {
		if (pinned) {
			close();
			return;
		}

		setPinned(true);
		show();
	}, [close, pinned, show]);

	const handleClickAway = useCallback(
		(event: MouseEvent | TouchEvent) => {
			if (event.target instanceof Node && contentRef.current?.contains(event.target) === true) {
				return;
			}
			close();
		},
		[close],
	);

	const setTriggerRef = useCallback((node: HTMLElement | null) => {
		triggerRef.current = node;
	}, []);

	const setContentRef = useCallback((node: HTMLDivElement | null) => {
		contentRef.current = node;
		resizeObserverRef.current?.disconnect();
		resizeObserverRef.current = null;
		if (node === null || typeof ResizeObserver === 'undefined') {
			return;
		}

		const observer = new ResizeObserver(() => {
			void popperRef.current?.update();
		});
		observer.observe(node);
		resizeObserverRef.current = observer;
	}, []);

	// A pinned preview stays open after focus leaves the trigger, so Escape is heard document-wide.
	useEffect(() => {
		if (!open) {
			return;
		}

		const ownerDocument = triggerRef.current?.ownerDocument ?? document;
		const handleEscape = (event: globalThis.KeyboardEvent) => {
			if (event.key !== 'Escape') {
				return;
			}

			event.stopPropagation();
			close();
			if (contentRef.current?.contains(ownerDocument.activeElement) === true) {
				triggerRef.current?.focus();
			}
		};

		ownerDocument.addEventListener('keydown', handleEscape);
		return () => ownerDocument.removeEventListener('keydown', handleEscape);
	}, [close, open]);

	const handleTriggerKeyDown = useCallback(
		(event: KeyboardEvent<HTMLElement>) => {
			if (event.key === 'Escape') {
				event.stopPropagation();
				close();
			}
		},
		[close],
	);

	return (
		<ClickAwayListener onClickAway={handleClickAway}>
			<Tooltip
				open={open}
				onOpen={show}
				onClose={handleTooltipClose}
				title={title(close)}
				placement={placement}
				describeChild
				slotProps={{
					popper: {
						popperRef,
						modifiers: [
							{
								name: 'flip',
								options: { fallbackPlacements, padding: TOOLTIP_VIEWPORT_PADDING },
							},
							{
								name: 'preventOverflow',
								options: {
									mainAxis: true,
									altAxis: true,
									rootBoundary: 'viewport',
									padding: TOOLTIP_VIEWPORT_PADDING,
									tether: false,
								},
							},
						],
						style: { zIndex },
					},
					tooltip: {
						ref: setContentRef,
						className:
							'!max-h-[calc(100dvh-1rem)] !max-w-[min(32rem,calc(100vw-1rem))] overflow-y-auto overscroll-contain',
					},
				}}
			>
				{children({
					ref: setTriggerRef,
					'aria-expanded': open,
					'aria-haspopup': 'true',
					onClick: handleTriggerClick,
					onFocus: show,
					onKeyDown: handleTriggerKeyDown,
				})}
			</Tooltip>
		</ClickAwayListener>
	);
}
